import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import { getApi, patchApi } from 'views/services/api';
import PaymentDetailsStep from '../../Room/ReserveRoomComponents/PaymentDetailsStep';

/**
 * Abono contra el saldo pendiente de una reserva.
 *
 * Los pendientes por concepto (habitación / restaurante / Sego) ya NO se mandan
 * desde acá: los calcula el servidor en services/folioService.js. Mandarlos
 * desde el navegador era la causa de que el saldo de restaurante y de Sego
 * nunca bajara, porque cada pago del split recibía los mismos pendientes.
 */
const PendingPaymentModal = ({ open, handleClose, reservationId, pendingAmount, fetchReservationData }) => {
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const hotelId = hotel?.hotelId;

  const [exchangeRates, setExchangeRates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showSplitPayments, setShowSplitPayments] = useState(false);
  const [splitPayments, setSplitPayments] = useState([]);

  useEffect(() => {
    const fetchExchangeRates = async () => {
      if (!hotelId) return;
      try {
        const response = await getApi(`api/exchange/viewall/${hotelId}`);
        const activeRates = response.data?.filter((rate) => rate.isActive);
        setExchangeRates(activeRates || []);
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
      }
    };

    if (open) {
      fetchExchangeRates();
      setShowSplitPayments(false);
      setSplitPayments([]);
    }
  }, [open, hotelId]);

  const handleTotalAmount = (advanceValue, setFieldValue) => {
    if (advanceValue === '') {
      setFieldValue('advanceAmount', '');
      return;
    }
    const numAdvance = parseFloat(advanceValue);
    if (isNaN(numAdvance) || numAdvance < 0) return;

    if (numAdvance <= pendingAmount) {
      setFieldValue('advanceAmount', advanceValue);
    } else {
      setFieldValue('advanceAmount', pendingAmount.toString());
      toast.warning('El pago no puede ser mayor a la deuda pendiente');
    }
  };

  const handleAddSplitPayment = () => {
    const newPayment = {
      id: Date.now(),
      paymentMethod: '',
      amount: '',
      currency: 'USD',
      exchangeRateUsed: 1,
      amountInForeignCurrency: 0,
      batchNumber: '',
      transactionNumber: ''
    };
    setSplitPayments([...splitPayments, newPayment]);
  };

  const handleUpdateSplitPayment = (paymentId, field, value) => {
    setSplitPayments(
      splitPayments.map((payment) => {
        if (payment.id === paymentId) {
          const updatedPayment = { ...payment, [field]: value };
          if (field === 'currency') {
            if (value === 'USD') {
              updatedPayment.exchangeRateUsed = 1;
              updatedPayment.amountInForeignCurrency = 0;
            } else {
              const rate = exchangeRates.find((r) => r.abbreviation === value);
              if (rate) {
                updatedPayment.exchangeRateUsed = rate.rate;
                if (updatedPayment.amount) {
                  updatedPayment.amountInForeignCurrency = (parseFloat(updatedPayment.amount) * rate.rate).toFixed(2);
                }
              }
            }
          }
          if (field === 'amount' && updatedPayment.currency !== 'USD') {
            updatedPayment.amountInForeignCurrency = (parseFloat(value || 0) * updatedPayment.exchangeRateUsed).toFixed(2);
          }
          return updatedPayment;
        }
        return payment;
      })
    );
  };

  const getTotalPaid = () => {
    const total = splitPayments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    return parseFloat(total.toFixed(2));
  };

  const areSplitPaymentsValid = (advanceAmount) => {
    if (!showSplitPayments) return true;
    if (splitPayments.length === 0) return false;

    const totalPaid = getTotalPaid();
    const advance = parseFloat(advanceAmount) || 0;

    if (advance <= 0) return false;

    const allPaymentsComplete = splitPayments.every((p) => p.paymentMethod && parseFloat(p.amount) > 0);
    const totalsMatch = Math.abs(totalPaid - advance) < 0.01;

    return allPaymentsComplete && totalsMatch;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);

    try {
      const advanceAmt = parseFloat(values.advanceAmount) || 0;
      if (advanceAmt <= 0) {
        toast.warning('Debe ingresar un monto a abonar válido');
        setLoading(false);
        setSubmitting(false);
        return;
      }

      let payload = {
        isMultiple: showSplitPayments
      };

      if (showSplitPayments) {
        if (!areSplitPaymentsValid(values.advanceAmount)) {
          toast.warning('Los pagos divididos no coinciden con el monto o están incompletos');
          setLoading(false);
          setSubmitting(false);
          return;
        }
        payload.splitPayments = splitPayments;
      } else {
        if (!values.paymentMethod) {
          toast.warning('Debe seleccionar un método de pago');
          setLoading(false);
          setSubmitting(false);
          return;
        }
        payload = {
          ...payload,
          paymentMethod: values.paymentMethod,
          amount: advanceAmt,
          currency: values.paymentCurrency,
          exchangeRateUsed: values.exchangeRateUsed,
          amountInForeignCurrency: values.amountInForeignCurrency,
          batchNumber: values.batchNumber,
          transactionNumber: values.transactionNumber
        };
      }

      const response = await patchApi(`api/reservation/addpayment/${reservationId}`, payload);

      if (response.status === 200) {
        toast.success('Pago registrado exitosamente');
        fetchReservationData();
        handleClose();
      } else {
        // El servidor rechaza el cobro con un motivo concreto: caja cerrada,
        // tasa vencida, método inválido. Mostrarlo tal cual le dice al
        // recepcionista qué hacer.
        toast.error(response?.response?.data?.error || 'Error al registrar el pago');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.error || 'Ocurrió un error en el servidor');
    }

    setLoading(false);
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Abonar / Pagar Saldo Pendiente</DialogTitle>
      
      <Formik
        enableReinitialize
        initialValues={{
          paymentMethod: '',
          paymentCurrency: 'USD',
          exchangeRateUsed: 1,
          amountInForeignCurrency: 0,
          batchNumber: '',
          transactionNumber: '',
          advanceAmount: pendingAmount > 0 ? pendingAmount.toString() : '0',
          totalAmount: '0'
        }}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, submitForm, isSubmitting }) => (
          <Form>
            <DialogContent dividers sx={{ backgroundColor: '#f9fafb' }}>
              <PaymentDetailsStep
                values={values}
                setFieldValue={setFieldValue}
                showSplitPayments={showSplitPayments}
                setShowSplitPayments={setShowSplitPayments}
                splitPayments={splitPayments}
                setSplitPayments={setSplitPayments}
                amount={pendingAmount.toFixed(2)}
                currency="USD"
                exchangeRates={exchangeRates}
                handleTotalAmount={handleTotalAmount}
                handleAddSplitPayment={handleAddSplitPayment}
                handleUpdateSplitPayment={handleUpdateSplitPayment}
                getTotalPaid={getTotalPaid}
                areSplitPaymentsValid={areSplitPaymentsValid}
                promoCode={null}
                setPromoCode={() => {}}
                discountAmount={0}
                finalAmount={pendingAmount}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2, backgroundColor: 'white' }}>
              <Button onClick={handleClose} disabled={loading || isSubmitting}>
                Cancelar
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={submitForm} 
                disabled={loading || isSubmitting || pendingAmount <= 0}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                Registrar Pago
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default PendingPaymentModal;
